import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
export const makeVariablePattern = ()=>/\{\{([^}]+)\}\}/g;
export const VARIABLE_PRESENCE_PATTERN = /\{\{[^}]+\}\}/;
const MAX_VARIABLE_LABEL_LENGTH = 200;
const MAX_VARIABLE_DEFAULT_VALUE_LENGTH = 1000;
const HTML_TAG_PATTERN = /<\/?[a-zA-Z]/;
export function validateTemplateVariableLabel(label) {
    const trimmed = label.trim();
    if (trimmed.length === 0) return null;
    if (trimmed.length > MAX_VARIABLE_LABEL_LENGTH) return 'Label exceeds maximum length';
    if (HTML_TAG_PATTERN.test(trimmed)) return 'Label contains invalid characters';
    return null;
}
export function validateTemplateVariableDefaultValue(value) {
    if (value.length > MAX_VARIABLE_DEFAULT_VALUE_LENGTH) return 'Default value exceeds maximum length';
    if (HTML_TAG_PATTERN.test(value)) return 'Default value contains invalid characters';
    return null;
}
export const SYSTEM_VARIABLE_CATALOG = [
    {
        category: 'Client',
        label: 'Client Company Name'
    },
    {
        category: 'Client',
        label: 'Client First Name'
    },
    {
        category: 'Client',
        label: 'Client Last Name'
    },
    {
        category: 'Client',
        label: 'Client Full Name'
    },
    {
        category: 'Client',
        label: 'Client Email'
    },
    {
        category: 'My Business',
        label: 'My Company Name'
    },
    {
        category: 'Agreement',
        label: 'Agreement Name'
    },
    {
        category: 'Agreement',
        label: 'Effective Date'
    },
    {
        category: 'Agreement',
        label: "Today's Date"
    },
    {
        category: 'Agreement',
        label: 'Current Month'
    },
    {
        category: 'Agreement',
        label: 'Current Year'
    }
];
const normalizedSystemLabels = new Set(SYSTEM_VARIABLE_CATALOG.map((entry)=>entry.label.toLowerCase()));
export function normalizeVariableLabel(label) {
    return label.toLowerCase();
}
export function isSystemVariable(label) {
    return normalizedSystemLabels.has(normalizeVariableLabel(label));
}
export function getSystemVariableCategory(label) {
    var _SYSTEM_VARIABLE_CATALOG_find;
    const normalized = normalizeVariableLabel(label);
    return (_SYSTEM_VARIABLE_CATALOG_find = SYSTEM_VARIABLE_CATALOG.find((entry)=>normalizeVariableLabel(entry.label) === normalized)) === null || _SYSTEM_VARIABLE_CATALOG_find === void 0 ? void 0 : _SYSTEM_VARIABLE_CATALOG_find.category;
}
export function extractVariableLabels(text) {
    const seen = new Set();
    const labels = [];
    extractLabelsFromText(text, seen, labels);
    return labels;
}
function extractLabelsFromText(text, seen, labels) {
    for (const match of text.matchAll(makeVariablePattern())){
        const label = match[1].trim();
        const normalized = normalizeVariableLabel(label);
        if (label.length > 0 && label.length <= MAX_VARIABLE_LABEL_LENGTH && !seen.has(normalized)) {
            seen.add(normalized);
            labels.push(label);
        }
    }
}
function parseDeltaInput(richText) {
    if (!richText) return null;
    if (typeof richText === 'string') {
        if (!richText.trim()) return null;
        try {
            return parseDeltaInput(JSON.parse(richText));
        } catch (unused) {
            return null;
        }
    }
    if (typeof richText !== 'object' || richText === null || Array.isArray(richText)) return null;
    const obj = richText;
    if (obj.type === 'quill-delta' && obj.value) {
        const inner = obj.value;
        if (Array.isArray(inner.ops)) {
            return {
                delta: inner,
                wrapper: obj
            };
        }
    }
    if (Array.isArray(obj.ops)) {
        return {
            delta: obj,
            wrapper: null
        };
    }
    return null;
}
function toDelta(richText) {
    var _ref;
    var _parseDeltaInput;
    return (_ref = (_parseDeltaInput = parseDeltaInput(richText)) === null || _parseDeltaInput === void 0 ? void 0 : _parseDeltaInput.delta) !== null && _ref !== void 0 ? _ref : null;
}
export function extractVariableLabelsFromDelta(richText) {
    const delta = toDelta(richText);
    if (!(delta === null || delta === void 0 ? void 0 : delta.ops)) {
        return [];
    }
    const seen = new Set();
    const labels = [];
    for (const op of delta.ops){
        if (typeof op.insert === 'string') {
            extractLabelsFromText(op.insert, seen, labels);
        } else if (op.insert && typeof op.insert === 'object') {
            const templateVar = op.insert['template-variable'];
            if (templateVar === null || templateVar === void 0 ? void 0 : templateVar.label) {
                const normalized = normalizeVariableLabel(templateVar.label);
                if (!seen.has(normalized)) {
                    seen.add(normalized);
                    labels.push(templateVar.label);
                }
            }
        }
    }
    return labels;
}
function formatDate(date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
function formatMonthName(date) {
    return date.toLocaleDateString('en-US', {
        month: 'long'
    });
}
export function resolveSystemVariable(label, context) {
    const normalized = normalizeVariableLabel(label);
    switch(normalized){
        case 'client company name':
            return context.clientCompanyName;
        case 'client first name':
            return context.clientFirstName;
        case 'client last name':
            return context.clientLastName;
        case 'client full name':
            if (context.clientFirstName && context.clientLastName) {
                return "".concat(context.clientFirstName, " ").concat(context.clientLastName);
            }
            return context.clientFirstName || context.clientLastName || undefined;
        case 'client email':
            return context.clientEmail;
        case 'my company name':
            return context.vendorBusinessName;
        case 'agreement name':
            return context.agreementName;
        case 'effective date':
            return context.effectiveDate ? formatDate(context.effectiveDate) : undefined;
        case "today's date":
            return formatDate(context.currentDate);
        case 'current month':
            return formatMonthName(context.currentDate);
        case 'current year':
            return String(context.currentDate.getFullYear());
        default:
            return undefined;
    }
}
export function resolveVariablesInText(text, resolvedValues) {
    return text.replace(makeVariablePattern(), (fullMatch, label)=>{
        var _resolvedValues_normalizeVariableLabel;
        const trimmed = label.trim();
        return (_resolvedValues_normalizeVariableLabel = resolvedValues[normalizeVariableLabel(trimmed)]) !== null && _resolvedValues_normalizeVariableLabel !== void 0 ? _resolvedValues_normalizeVariableLabel : fullMatch;
    });
}
function resolveDeltaOps(delta, resolvedValues) {
    var _delta_ops;
    const resolvedOps = ((_delta_ops = delta.ops) !== null && _delta_ops !== void 0 ? _delta_ops : []).map((op)=>{
        if (typeof op.insert === 'string') {
            if (!VARIABLE_PRESENCE_PATTERN.test(op.insert)) return op;
            return _object_spread_props(_object_spread({}, op), {
                insert: resolveVariablesInText(op.insert, resolvedValues)
            });
        }
        if (op.insert && typeof op.insert === 'object') {
            const templateVar = op.insert['template-variable'];
            if (templateVar === null || templateVar === void 0 ? void 0 : templateVar.label) {
                const resolved = resolvedValues[normalizeVariableLabel(templateVar.label)];
                if (resolved !== undefined) {
                    return _object_spread_props(_object_spread({}, op), {
                        insert: resolved
                    });
                }
                return op;
            }
        }
        return op;
    });
    return _object_spread_props(_object_spread({}, delta), {
        ops: resolvedOps
    });
}
export function resolveVariablesInDelta(richText, resolvedValues) {
    var _parseResult_delta;
    const parseResult = parseDeltaInput(richText);
    if (!(parseResult === null || parseResult === void 0 ? void 0 : (_parseResult_delta = parseResult.delta) === null || _parseResult_delta === void 0 ? void 0 : _parseResult_delta.ops)) {
        return richText;
    }
    const resolvedDelta = resolveDeltaOps(parseResult.delta, resolvedValues);
    if (typeof richText === 'string') {
        if (parseResult.wrapper) {
            return JSON.stringify(_object_spread_props(_object_spread({}, parseResult.wrapper), {
                value: resolvedDelta
            }));
        }
        return JSON.stringify(resolvedDelta);
    }
    if (parseResult.wrapper) {
        return _object_spread_props(_object_spread({}, parseResult.wrapper), {
            value: resolvedDelta
        });
    }
    return resolvedDelta;
}
export function filterVariablesBySearch(variables, search) {
    const trimmed = search.trim();
    if (!trimmed) {
        return variables;
    }
    const normalizedSearch = normalizeVariableLabel(trimmed);
    return variables.filter((variable)=>normalizeVariableLabel(variable.label).includes(normalizedSearch));
}
